/**
 * Roles — person ↔ resource type, the join the capacity model was missing.
 *
 * Harry, 2026-08-24: a unit of work rolls back to a resource, usually a person
 * in a role with a set of characteristics. Roster, Matrix and By-skill already
 * captured the characteristics — hours a week, capability, level — and none of
 * it reached an allocation decision, because nothing said which pool a person's
 * hours belong to. This view says it.
 *
 * Hours are per assignment, so a person split 20 h engineering / 10 h pm never
 * double-counts across pools. A resource need not be an app user: a generic
 * role placeholder ("Senior Backend Eng ×2") is what keeps rough-cut planning
 * honest instead of forcing fake people onto the roster.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, Trash2, UserPlus, X } from 'lucide-react';
import {
  createPlanningResource,
  createRoleAssignment,
  deletePlanningResource,
  deleteRoleAssignment,
  ensureResourcesForUsers,
  getRoleAssignments,
  updateRoleAssignment,
} from '../../lib/planningRosterService';
import {
  RESOURCE_KIND_LABELS,
  type PlanningResource,
  type PlanningResourceKind,
  type PlanningRoleAssignment,
} from '../../lib/planningRoster';
import { useResourceTypesStore } from '../../stores/prioritization/resourceTypesStore';

interface Member {
  userId: string;
  name: string;
  email: string;
}

const KIND_BADGE: Record<PlanningResourceKind, string> = {
  person: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  generic: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border border-[var(--mustard)]',
  external: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
};

// ─── One role chip ────────────────────────────────────────────────────────────

function RoleChip({
  assignment,
  label,
  onChanged,
  onRemoved,
}: {
  assignment: PlanningRoleAssignment;
  label: string;
  onChanged: (next: PlanningRoleAssignment) => void;
  onRemoved: () => void;
}) {
  const [draft, setDraft] = useState(String(assignment.hoursPerWeek));

  useEffect(() => {
    setDraft(String(assignment.hoursPerWeek));
  }, [assignment.hoursPerWeek]);

  const save = async () => {
    const hours = parseFloat(draft);
    if (!Number.isFinite(hours) || hours < 0 || hours === assignment.hoursPerWeek) {
      setDraft(String(assignment.hoursPerWeek));
      return;
    }
    const next = await updateRoleAssignment(assignment.id, { hoursPerWeek: hours });
    onChanged(next);
  };

  const window =
    assignment.effectiveFrom || assignment.effectiveTo
      ? `${assignment.effectiveFrom ?? '…'} → ${assignment.effectiveTo ?? '…'}`
      : null;

  return (
    <span
      className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100 group"
      title={window ? `Effective ${window}` : 'Effective for the whole horizon'}
    >
      {label}
      <span className="text-primary-300">·</span>
      <input
        type="number"
        min={0}
        step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        className="w-9 bg-transparent text-right outline-none focus:bg-[var(--bg-raised)] focus:ring-1 focus:ring-primary-300 rounded"
        aria-label={`${label} hours per week`}
      />
      <span className="text-primary-400">h/wk</span>
      {assignment.dedicatedToPriorityId && (
        <span className="text-[var(--mustard)]" title="Reserved for one priority — invisible to general demand">
          ⚑
        </span>
      )}
      <button
        type="button"
        onClick={onRemoved}
        className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 hover:text-[var(--coral)]"
        aria-label={`Remove ${label} role`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Add-role control ─────────────────────────────────────────────────────────

function AddRole({
  resource,
  workspaceId,
  assigned,
  onAdded,
}: {
  resource: PlanningResource;
  workspaceId: string;
  assigned: Set<string>;
  onAdded: (assignment: PlanningRoleAssignment) => void;
}) {
  const [busy, setBusy] = useState(false);
  // getEnabledTypes() builds a NEW array on every call, so passing it as a
  // zustand selector makes the snapshot never compare equal — the component
  // re-renders forever (React #185). Read once from getState(), the way every
  // other consumer of this store does.
  const resourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);
  const available = resourceTypes.filter((rt) => !assigned.has(rt.key));

  if (available.length === 0) return null;

  const add = async (resourceType: string) => {
    setBusy(true);
    try {
      onAdded(
        await createRoleAssignment({
          workspaceId,
          resourceId: resource.id,
          resourceType,
          hoursPerWeek: 0,
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <select
      value=""
      disabled={busy}
      onChange={(e) => {
        if (e.target.value) void add(e.target.value);
      }}
      className="text-xs border border-dashed border-[var(--rule)] rounded-full px-2 py-0.5 text-[var(--text-ter)] hover:border-primary-400 hover:text-primary-600 bg-transparent"
      aria-label={`Add a role for ${resource.displayName}`}
    >
      <option value="">+ role</option>
      {available.map((rt) => (
        <option key={rt.key} value={rt.key}>
          {rt.label}
        </option>
      ))}
    </select>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function PlanningRolesView({
  workspaceId,
  members,
}: {
  workspaceId: string;
  members: Member[];
}) {
  const [resources, setResources] = useState<PlanningResource[]>([]);
  const [assignments, setAssignments] = useState<PlanningRoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<PlanningResourceKind>('generic');
  // Same rule as AddRole above: never select getEnabledTypes() directly.
  const resourceTypes = useMemo(() => useResourceTypesStore.getState().getEnabledTypes(), []);

  const typeLabel = useCallback(
    (key: string) => resourceTypes.find((rt) => rt.key === key)?.label ?? key,
    [resourceTypes],
  );

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Every workspace member gets a planning-side identity automatically —
      // nobody re-enters a team the app already knows about.
      const [seeded, asg] = await Promise.all([
        ensureResourcesForUsers(
          workspaceId,
          members.map((m) => ({ userId: m.userId, name: m.name || m.email || 'Unnamed' })),
        ),
        getRoleAssignments(workspaceId),
      ]);
      setResources(seeded);
      setAssignments(asg);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, members]);

  useEffect(() => {
    void load();
  }, [load]);

  const assignmentsByResource = useMemo(() => {
    const map = new Map<string, PlanningRoleAssignment[]>();
    for (const a of assignments) {
      const list = map.get(a.resourceId) ?? [];
      list.push(a);
      map.set(a.resourceId, list);
    }
    return map;
  }, [assignments]);

  const poolTotals = useMemo(() => {
    const activeIds = new Set(resources.filter((r) => r.active).map((r) => r.id));
    const totals = new Map<string, number>();
    for (const a of assignments) {
      if (!activeIds.has(a.resourceId) || a.dedicatedToPriorityId) continue;
      totals.set(a.resourceType, (totals.get(a.resourceType) ?? 0) + a.hoursPerWeek);
    }
    return [...totals.entries()].sort((a, b) => b[1] - a[1]);
  }, [assignments, resources]);

  const addResource = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await createPlanningResource({ workspaceId, displayName: name, kind: newKind });
      setResources((prev) => [...prev, created].sort((a, b) => a.displayName.localeCompare(b.displayName)));
      setNewName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeResource = async (resource: PlanningResource) => {
    if (resource.userId) return; // member rows follow workspace membership, not this view
    if (!confirm(`Remove ${resource.displayName} and its roles from the roster?`)) return;
    await deletePlanningResource(resource.id);
    setResources((prev) => prev.filter((r) => r.id !== resource.id));
    setAssignments((prev) => prev.filter((a) => a.resourceId !== resource.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-3 text-sm text-[var(--coral)]">{error}</div>
      )}

      <p className="text-xs text-[var(--text-ter)]">
        Hours are per role, so someone split across two roles never double-counts. A scenario reads
        these pools once its capacity source is set to the roster —{' '}
        <Link to="/planning/engine/capacity" className="text-primary-600 hover:underline">
          Resource budget
        </Link>{' '}
        shows the roll-up and who is behind each number.
      </p>

      <div className="border border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)]">
        <div className="grid grid-cols-[minmax(180px,1fr)_2fr_90px_40px] gap-3 px-4 py-2 bg-[var(--surface)] border-b border-[var(--rule)] text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider">
          <span>Resource</span>
          <span>Roles</span>
          <span className="text-right">Total</span>
          <span />
        </div>

        {resources.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-[var(--text-ter)]">
            No resources yet. Workspace members appear here automatically; add generic roles for
            positions you plan against before you know names.
          </p>
        )}

        {resources.map((resource) => {
          const rows = assignmentsByResource.get(resource.id) ?? [];
          const total = rows.reduce((sum, a) => sum + a.hoursPerWeek, 0);
          return (
            <div
              key={resource.id}
              className="grid grid-cols-[minmax(180px,1fr)_2fr_90px_40px] gap-3 px-4 py-2.5 border-b border-[var(--rule)] last:border-0 items-center"
            >
              <div className="min-w-0">
                <p
                  className={`text-sm truncate ${resource.active ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)] line-through'}`}
                >
                  {resource.displayName}
                </p>
                <span
                  className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${KIND_BADGE[resource.kind]}`}
                >
                  {RESOURCE_KIND_LABELS[resource.kind]}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {rows.map((a) => (
                  <RoleChip
                    key={a.id}
                    assignment={a}
                    label={typeLabel(a.resourceType)}
                    onChanged={(next) =>
                      setAssignments((prev) => prev.map((x) => (x.id === next.id ? next : x)))
                    }
                    onRemoved={() => {
                      void deleteRoleAssignment(a.id);
                      setAssignments((prev) => prev.filter((x) => x.id !== a.id));
                    }}
                  />
                ))}
                <AddRole
                  resource={resource}
                  workspaceId={workspaceId}
                  assigned={new Set(rows.map((a) => a.resourceType))}
                  onAdded={(a) => setAssignments((prev) => [...prev, a])}
                />
              </div>

              <span className="text-sm text-[var(--text-sec)] text-right tabular-nums">
                {total > 0 ? `${total} h/wk` : '—'}
              </span>

              <div className="flex justify-end">
                {!resource.userId && (
                  <button
                    type="button"
                    onClick={() => void removeResource(resource)}
                    className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                    aria-label={`Remove ${resource.displayName}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add a resource that is not an app user */}
      <div className="flex flex-wrap items-center gap-2">
        <UserPlus className="h-4 w-4 text-[var(--text-ter)]" />
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void addResource();
          }}
          placeholder="Senior Backend Eng ×2"
          className="px-2.5 py-1.5 text-sm border border-[var(--rule)] focus:border-primary-300 focus:ring-2 focus:ring-primary-100 outline-none"
        />
        <select
          value={newKind}
          onChange={(e) => setNewKind(e.target.value as PlanningResourceKind)}
          className="px-2 py-1.5 text-sm border border-[var(--rule)]"
        >
          <option value="generic">Generic role</option>
          <option value="external">External</option>
        </select>
        <button
          type="button"
          onClick={() => void addResource()}
          disabled={!newName.trim()}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {/* What the pools add up to, before any scenario horizon or buffer */}
      {poolTotals.length > 0 && (
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
          <p className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wider mb-2">
            Weekly supply by resource type
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {poolTotals.map(([type, hours]) => (
              <span key={type} className="text-sm text-[var(--text-sec)]">
                {typeLabel(type)}{' '}
                <span className="font-semibold tabular-nums">{hours} h/wk</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-2">
            Before any scenario horizon, effective window, or capacity buffer. Reserved (dedicated)
            hours are excluded — they only satisfy their named priority.
          </p>
        </div>
      )}
    </div>
  );
}
